import { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { ENDPOINTS } from '../../../../General/Utils/Endpoints';
import { Models } from '../../../../General/Utils/ModelsGeneral';
import ConfirmDeleteDialog from '../../../../security/ConfirmDeleteDialog';
import { normalizeValue } from '../../../../../utils/Texts';
import api from '../../../../../api';
import Modal from './EmpleadosModal';
import Tabs from './EmpleadosTabs';
import sigmaDelete from '../../../../Pages/img/Borrar.png';
import sigmaEdit from '../../../../Pages/img/Editar.png';
import AlertDialog from '../../../../Lms/Components/Shared/AlertDialog';
import { isSessionSuperAdmin, getSessionEmpresaId } from '../../../../../utils/SessionUser';
import { useResourcePermissions } from '../../../../../utils/useResourcePermissions';

const PAGE_SIZE = 10;
const nombreTabla = "Catálogo de Empleados";
const endpointRead  = ENDPOINTS.empleadosSecurity; // GET: filtrado multi-tenant
const endpointWrite = ENDPOINTS.empleados;          // POST / PUT
const endpointById  = ENDPOINTS.empleadosById;      // DELETE /:id
const modelo = Models.empleado;

const maps = {
  mapCreate: (f) => ({
    idPersona: Number(f.idPersona) || 0,
    numero_Empleado: f.numeroEmpleado
  }),
  mapUpdate: (id, f) => ({
    idEmpleado: id,
    idPersona: Number(f.idPersona) || 0,
    numero_Empleado: f.numeroEmpleado
  }),
};

export default function Catalog() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('new');
  const [selectedId, setSelectedId] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ id: 0, name: '' });
  const [editData, setEditData] = useState(null);
  const [editingRowId, setEditingRowId] = useState(null);
  const [page, setPage] = useState(1);
  const [rowsPersonas, setPersonas] = useState([]);
  const [alertDialog, setAlertDialog] = useState({ open: false, title: '', message: '', variant: 'info' });
  const [showChildData, setShowChildData] = useState(false);

  // ── Permisos granulares por recurso ──────────────────────────────────────
  const { puedeCrear, puedeEditar, puedeEliminar } = useResourcePermissions();

  const editorRef = useRef(null);

  useEffect(() => {
    if (modalOpen && editorRef.current) {
      editorRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [modalOpen, selectedId]);

  // 1) Cargar empleados
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const finalFilter = showChildData ? 'hijas' : 'propias';
      const res = await api.get(endpointRead(finalFilter));
      setRows((res.data || []).map((x) => modelo(x)));
    } catch (e) {
      console.error(e);
      setAlertDialog({ open: true, title: 'Error', message: 'Error al obtener los datos', variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [showChildData]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // 2) Cargar personas (SOLO UNA VEZ)
  const loadPersonas = async () => {
    try {
      const res = await api.get(ENDPOINTS.personas);
      setPersonas((res.data || []).map((x) => Models.personas(x)));
    } catch (e) {
      console.error(e);
      setAlertDialog({ open: true, title: 'Error', message: 'Error al obtener las personas', variant: 'error' });
    }
  };
  useEffect(() => { loadPersonas(); }, []); // ← importante: deps vacías

  // 3) Mapa por idPersona para lookup rápido y seguro
  const personasById = useMemo(() => {
    const map = new Map();
    for (const p of rowsPersonas) {
      const id = Number(p.idPersona);
      map.set(id, p);
    }
    return map;
  }, [rowsPersonas]);

  // 4) Normalización de búsqueda
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const sessionEmpresaId = getSessionEmpresaId();
    const isSuperAdmin = isSessionSuperAdmin();

    // Aislamiento Multi-Tenant y Seguridad de Hijas
    let data = rows;
    if (!isSuperAdmin) {
      data = rows.filter(r => {
        const isOwn = !r.idEmpresaEfectiva || Number(r.idEmpresaEfectiva) === sessionEmpresaId;
        if (isOwn) return true;

        // Es de una empresa hija: solo se ve si el botón de "Ver Hijas" está activo
        return showChildData;
      });
    }

    // Ordenamiento Jerárquico
    data.sort((a, b) => {
      const aIsOwn = !a.idEmpresaEfectiva || Number(a.idEmpresaEfectiva) === sessionEmpresaId;
      const bIsOwn = !b.idEmpresaEfectiva || Number(b.idEmpresaEfectiva) === sessionEmpresaId;
      if (aIsOwn && !bIsOwn) return -1;
      if (!aIsOwn && bIsOwn) return 1;

      // Por nombre de empresa
      const aNomEmp = a.nombreEmpresa || '';
      const bNomEmp = b.nombreEmpresa || '';
      const empComp = aNomEmp.localeCompare(bNomEmp);
      if (empComp !== 0) return empComp;

      // Por nombre del registro
      const personaA = personasById.get(Number(a.idPersona));
      const personaB = personasById.get(Number(b.idPersona));
      const nomA = personaA ? `${personaA.nombre} ${personaA.apellidoPaterno}` : '';
      const nomB = personaB ? `${personaB.nombre} ${personaB.apellidoPaterno}` : '';
      return nomA.localeCompare(nomB);
    });

    if (!q) return data;
    return data.filter((r) => {
      const num = String(r.numeroEmpleado || '').toLowerCase();
      const idp = String(r.idPersona ?? '').toLowerCase();

      const persona = personasById.get(Number(r.idPersona));
      const personaNombre = persona
        ? `${persona.nombre} ${persona.apellidoPaterno} ${persona.apellidoMaterno || ''}`.toLowerCase()
        : '';

      // nombreEmpresa viene resuelto desde el SP (UO → Personas fallback)
      const empresaNombre = (r.nombreEmpresa || '').toLowerCase();

      return num.includes(q) || idp.includes(q) || personaNombre.includes(q) || empresaNombre.includes(q);
    });
  }, [rows, query, personasById, showChildData]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);

  const paged = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const handleNew = () => {
    setSelectedId(null);
    setEditData(null);
    setModalMode('new');
    setModalOpen(true);
    setEditingRowId(null);
  };

  const handleEdit = (id) => {
    const r = rows.find(x => x.idEmpleado === id);
    if (!r) {
      setAlertDialog({ open: true, title: 'Error', message: 'No se encontró la fila en memoria.', variant: 'error' });
      return;
    }
    setSelectedId(id);
    setEditData(r);
    setModalMode('edit');
    setModalOpen(true);
    setEditingRowId(id);
  };

  const askDelete = (id, name) => {
    setDeleteTarget({ id, name });
    setDeleteOpen(true);
  };

  const doDelete = async () => {
    try {
      setLoading(true);
      const url = typeof endpointById === 'function'
        ? endpointById(deleteTarget.id)
        : `${endpointById}/${deleteTarget.id}`;
      await api.delete(url);
      setDeleteOpen(false);
      setEditingRowId(null);
      await loadData();
    } catch (e) {
      console.error(e);
      setAlertDialog({ open: true, title: 'Error', message: 'Error al eliminar', variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (payload, mode) => {
    try {
      setLoading(true);
      if (mode === 'new') {
        // POST → endpoint base (no /security)
        const res = await api.post(endpointWrite, maps.mapCreate(payload));
        const newId = res?.data?.idEmpleado ?? (typeof res?.data === 'number' ? res.data : null);
        if (newId) setEditingRowId(newId);
      } else {
        // PUT → endpoint base
        await api.put(endpointWrite, maps.mapUpdate(selectedId, payload));
        setEditingRowId(selectedId);
      }
      setModalOpen(false);
      await loadPersonas();
      await loadData();
    } catch (e) {
      console.error(e);
      setAlertDialog({ open: true, title: 'Error', message: 'Error al guardar', variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const PageBtn = ({ label, onClick, active, disabled }) => (
    <button
      type="button"
      className={`roles-page-btn ${active ? 'is-active' : ''} ${disabled ? 'is-disabled' : ''}`}
      onClick={onClick}
      disabled={disabled}
    >{label}</button>
  );

  const pageNumbers = (() => {
    const maxToShow = 5;
    let start = Math.max(1, page - 2);
    let end = Math.min(totalPages, start + maxToShow - 1);
    start = Math.max(1, end - maxToShow + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  })();

  const startIndex = filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endIndex = Math.min(page * PAGE_SIZE, filtered.length);

  return (
    <div className="panel-sigma-main">
      <div className="roles-shell">
        <div className="roles-topbar">
          <h2 className="roles-title">{nombreTabla}</h2>

          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <input
              className="roles-search"
              placeholder="Buscar por nombre, número de empleado o ID persona…"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setPage(1); }}
            />

            <button
              type="button"
              className={`roles-btn ${showChildData ? 'is-active' : ''}`}
              onClick={() => { setShowChildData(!showChildData); setPage(1); }}
              title={showChildData ? 'Ocultar empleados de empresas hijas' : 'Ver empleados de empresas hijas'}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '0 15px',
                height: '40px',
                borderRadius: '8px',
                fontSize: '0.85rem',
                whiteSpace: 'nowrap',
                background: showChildData ? '#e2e8f0' : '#fff',
                border: '1px solid #cbd5e1',
                cursor: 'pointer'
              }}
            >
              {showChildData ? '👁️ Ocultar Hijas' : '👁️ Ver Hijas'}
            </button>
          </div>

          {puedeCrear && (
            <button className="btn-action" onClick={handleNew}>＋ Nuevo</button>
          )}
        </div>

        <div className="roles-table-wrap">
          <div className="roles-table-scroller">
            <table className="roles-table">
              <thead>
                <tr>
                  <th>Nombre De la Persona</th>
                  <th>Número Empleado</th>
                  <th>Empresa</th>
                  {puedeEditar && <th style={{ textAlign: 'center' }}>Editar</th>}
                  {puedeEliminar && <th style={{ textAlign: 'center' }}>Borrar</th>}
                </tr>
              </thead>
              <tbody>
                {paged.map((r) => {
                  // Lookup seguro
                  const persona = personasById.get(Number(r.idPersona));
                  const nombreParts = persona 
                    ? [persona.nombre, persona.apellidoPaterno, persona.apellidoMaterno]
                    : [r.nombre, r.apellidoPaterno, r.apellidoMaterno];
                  
                  const filteredParts = nombreParts.filter(Boolean);
                  const personaNombre = normalizeValue(
                    filteredParts.length > 0 ? filteredParts.join(' ') : '(sin nombre)'
                  );
                  const numero = normalizeValue(r.numeroEmpleado);

                  // nombreEmpresa viene del SP (UO → Personas.IdEmpresa fallback)
                  const empresaNombreLabel = r.nombreEmpresa
                    ? r.nombreEmpresa
                    : <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>Sin empresa</span>;

                  const isManagedByOther = r.empresaDelegada && !r.esAdminDelegado;

                  if (isManagedByOther) return null; // El SP ya los debería filtrar, pero por si acaso

                  return (
                    <tr key={r.idEmpleado} className={editingRowId === r.idEmpleado ? 'rk-row-active' : ''}>
                      <td>{personaNombre}</td>
                      <td><strong>{numero}</strong></td>
                      <td>{empresaNombreLabel}</td>
                      {puedeEditar && (
                        <td style={{ textAlign: 'center' }}>
                          <button
                            type="button"
                            className="rk-iconbtn rk-iconbtn--edit"
                            onClick={() => handleEdit(r.idEmpleado)}
                            aria-label="Editar"
                            title="Editar"
                          >
                            <img src={sigmaEdit} alt="" className="rk-ico" />
                          </button>
                        </td>
                      )}
                      {puedeEliminar && (
                        <td style={{ textAlign: 'center' }}>
                          <button
                            type="button"
                            className="rk-iconbtn rk-iconbtn--del"
                            onClick={() => {
                              setEditingRowId(null);
                              const name = r.numeroEmpleado || 'Empleado';
                              askDelete(r.idEmpleado, name);
                            }}
                            aria-label="Borrar"
                            title="Borrar"
                          >
                            <img src={sigmaDelete} alt="" className="rk-ico" />
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
                {!loading && paged.length === 0 && (<tr><td colSpan={3 + (puedeEditar ? 1 : 0) + (puedeEliminar ? 1 : 0)}>Sin resultados.</td></tr>)}
              </tbody>
            </table>
          </div>
        </div>

        <div className="roles-pagination">
          <div className="roles-pagination-left">
            {filtered.length > 0 ? (
              <span>Mostrando {startIndex}–{endIndex} de {filtered.length}</span>
            ) : (
              <span>0 resultados</span>
            )}
          </div>
          <div className="roles-pagination-right">
            <PageBtn label="‹" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} />
            {pageNumbers.map(n => (<PageBtn key={n} label={n} onClick={() => setPage(n)} active={n === page} />))}
            <PageBtn label="›" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} />
          </div>
        </div>

        {modalOpen && (
          <div className="roles-editor-slot" ref={editorRef}>
            {modalMode === 'new' && (
              <div className="person-tabs-disabled" style={{ marginBottom: 12 }}>
                <span>
                  Selecciona o crea un empleado y pulsa <b>Editar</b> para gestionar sus datos
                  relacionados.
                </span>
              </div>
            )}
            <Modal
              open={modalOpen}
              mode={modalMode}
              data={editData}
              inline
              onClose={() => setModalOpen(false)}
              onSubmit={handleSubmit}
              readOnly={!isSessionSuperAdmin() && editData && editData.idEmpresaEfectiva !== getSessionEmpresaId()}
            />
            {modalMode === 'edit' && editData?.idEmpleado > 0 && (
              <Tabs 
                personId={editData.idEmpleado} 
                readOnly={!isSessionSuperAdmin() && editData.idEmpresaEfectiva !== getSessionEmpresaId()}
                isDelegated={editData?.empresaDelegada}
                onRefresh={loadData}
                onClose={() => {
                  setModalOpen(false);
                  loadData();
                }}
              />
            )}
          </div>
        )}
      </div>

      <ConfirmDeleteDialog
        open={deleteOpen}
        roleName={deleteTarget.name}
        onClose={() => setDeleteOpen(false)}
        onConfirm={doDelete}
      />

      {alertDialog.open && (
        <AlertDialog
          open={alertDialog.open}
          onClose={() => setAlertDialog(prev => ({ ...prev, open: false }))}
          title={alertDialog.title}
          message={alertDialog.message}
          variant={alertDialog.variant}
        />
      )}
    </div>
  );
}
